import { Extend } from './internal/typeHelpers';

/**
 * This domain should correspond with the ones defined in Jira issues.
 * If the one you're looking for is missing, please add it here and in backoffice.
 */
type Domain =
  | 'access'
  | 'ai'
  | 'appsec'
  | 'audit-log'
  | 'auth'
  | 'bpm'
  | 'broadcasts'
  | 'calculated-fields'
  | 'component-overview'
  | 'core'
  | 'core-viewpoints'
  | 'dashboards'
  | 'design-system'
  | 'discover'
  | 'discover-in-core'
  | 'fields'
  | 'graphlake'
  | 'gremlin-query'
  | 'grid-editor'
  | 'growth'
  | 'i18n'
  | 'integrations'
  | 'inventory'
  | 'metamodel-module'
  | 'metamodel-visualizations'
  | 'navigator'
  | 'perspectives'
  | 'presentations'
  | 'reports'
  | 'scenarios'
  | 'search'
  | 'signal'
  | 'solutions'
  | 'surveys'
  | 'tasks'
  | 'transformations'
  | 'visibility'
  | 'visualizations';

// More scopes can be added if there's a need for them
type Scope =
  | 'tmp' // work in progress, beta features
  | 'internal' // not for customers
  | 'paid' // only for paid plans
  | 'phased' // phasing out, but a few customers are lagging
  | 'restricted'; // only allow for certain customers

type FeatureNameSegment = string;

/**
 * <domain>_<scope>_<feature-name>
 * See https://ardoqcom.atlassian.net/wiki/spaces/TECH/pages/3734667277/Decision+Standardized+naming+of+feature+flags
 */
export type FeatureFlagType = `${Domain}_${Scope}_${FeatureNameSegment}`;

export type Feature = {
  persistedId: string;
  label: string;
  requiresOrgAdmin?: boolean;
  isEnabledForTrial?: boolean;
  requiresArdoqEmployee?: boolean;
};

type StrictFeature = Extend<Feature, { persistedId: FeatureFlagType }>;

const features = {
  COPY_WORKSPACES_BETWEEN_ORGS: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'copy-workspaces-between-organizations',
    label: 'Copy workspaces between organizations',
  },
  PUBLIC_PRESENTATIONS: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'public-presentation',
    label: 'Public presentations',
    requiresOrgAdmin: true,
  },
  SURVEYS: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'surveys',
    label: 'Surveys',
    isEnabledForTrial: true,
  },
  WEBHOOKS: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'webhooks',
    label: 'Web hooks',
  },
  /** Deprecated view. */
  SHOW_OLD_RELATIONSHIP_VIEW_2: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'show-old-relationship-view-2',
    label: 'Old Relationship View / Network',
  },
  ROADMAP_FOR_HMCTS: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'roadmap-for-hmcts',
    label: 'Roadmap',
  },
  BUNDLE_MANAGEMENT: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'bundle-management',
    label: 'Bundle management',
    requiresOrgAdmin: true,
  },
  BUNDLE_COPY_ASYNC: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'bundle-copy-async',
    label: 'Bundle copy async',
  },
  AZURE_ENABLE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'azure-enable',
    label: 'Enable Azure integration',
  },
  CELONIS_ENABLE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'celonis-enable',
    label: 'Enable Celonis integration',
  },
  ARDOQ_IMPORT_ENABLE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'ardoq-import-enable',
    label: 'Enable Ardoq integration',
  },
  ARDOQ_REFERENCE_DATA_IMPORT_ENABLE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'ardoq-reference-data-import-enable',
    label: 'Enable Ardoq reference data integration',
  },
  ARDOQ_SAMPLE_DATA_IMPORT_ENABLE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'ardoq-sample-data-import-enable',
    label: 'Enable Ardoq sample data integration',
  },
  WEBHOOKS_ENABLE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'webhooks-enable',
    label: 'Enable WebHooks',
  },
  RESTAPI_ENABLE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'restapi-enable',
    label: 'Enable REST API',
  },
  SERVICENOW_EXPORT_ENABLE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'enable-servicenow-export',
    label: 'Enable Servicenow export integration',
  },
  SERVICENOW_GUIDED_EXPORT_ENABLE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'servicenow-guided-export-enable',
    label: 'Enable Servicenow export integration',
  },
  DISABLE_REST_API_V1_DOCS: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'disable-rest-api-v1-docs',
    label: 'Disable REST API v1 docs',
  },
  SCENARIOS_BETA: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'scenarios-beta',
    label: 'Scenarios Beta',
  },
  /** Viewpoint builder. Adds a "start set expansion" control that lets a traversal start from the selected components *and* their whole component-hierarchy subtree, without expressing it as a recursive parent-child path. */
  START_SET_EXPANSION_WITH_DESCENDANTS: {
    persistedId: 'core-viewpoints_tmp_start-set-expansion-with-descendants',
    label: 'Viewpoint builder: expand start set with descendants',
  },
  /** Opens a workspace with no saved startView using the user's most recently used eligible view style instead of the fixed default. */
  RECENTLY_USED_VIEW_STYLES: {
    persistedId: 'core-viewpoints_tmp_recently-used-view-styles',
    label: 'Open workspaces with no default view using recent usage',
  },
  /** Experimental view developed for hype generation. Essentially block diagram with isometric rendering to look 3D. */
  ISOMETRIC_BLOCK_DIAGRAM: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'isometricBlockDiagram',
    label: 'Isometric Block Diagram (BETA)',
  },
  BROADCASTS: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'broadcasts',
    label: 'Broadcasts',
  },
  SCIM_PROVISIONING: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'scim-provisioning',
    label: 'SCIM provisioning',
    requiresOrgAdmin: true,
  },
  GROUPING_RESEARCH: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'grouping-research',
    label: 'Grouping research',
  },
  GROUPING_BY_TYPE_NAMES: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'grouping-by-type-names',
    label: 'Group by type names',
  },
  ROOT_GROUPS_IN_CONTEXT_ONLY: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'root-groups-in-context-only',
    label:
      'Show only root groups containing the context component in Dependency Map and Timeline',
  },
  /** Allows users to save presentation slides, even for deprecated views. Enabled by special request for certain customers who weren't thrilled with the deprecated status of some views and the limitations imposed. */
  CAN_UPDATE_DEPRECATED_SLIDES: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'can-update-deprecated-slides',
    label: 'Permit updating presentation slides containing deprecated views',
  },
  IP_ALLOWLIST: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'ip-whitelisting',
    requiresOrgAdmin: true,
    label:
      'Enable IP blocking in the API. Org admins can add/edit allowlist IPs regardless of this flag; it only activates enforcement.',
  },
  HIERARCHICAL_TREEMAP: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'hierarchical-treemap',
    label: 'Hierarchical Treemap',
  },
  REFERENCE_FAB_PROTOTYPE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'reference-fab-prototype',
    label:
      'Enables the red "Reference fab" button for the new prototype Create Reference flow',
    requiresArdoqEmployee: true,
  },
  SEMANTIC_SEARCH: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'semantic-search',
    label: 'Enables semantic search in Discover',
  },
  CREATE_COMPONENTS_VIA_SURVEYS_IN_DISCOVER: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'create-components-via-surveys-in-discover',
    label: 'Create components via surveys in discover',
  },
  AUTOCOMPLETE_OPENAI: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'autocomplete-openai',
    label: 'Survey description assistant',
  },
  DISCOVER_PERSONALIZATION: {
    label: 'Discover personalization',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'discover-personalization',
  },
  EDITABLE_REFERENCES_IN_QUESTIONS: {
    label: 'Contributors can edit the references components in surveys',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'editable-references-in-questions',
  },
  SURVEY_IS_COMPLETE_BROADCAST_FILTER: {
    label: 'Survey is complete broadcast filter',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'survey-is-complete-broadcast-filter',
  },
  DISABLE_CONNECTED_PEOPLE: {
    label: 'Disable connected people',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'disable-connected-people',
  },
  BOOKMARKS: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'bookmarks',
    label: `Enable bookmarks, which are basically slides that can, but don't have to be a part of presentation.`,
  },
  DISABLE_DATE_TIME_RANGE: {
    label: 'Disable DateTimeRange fields creation',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'disable-date-time-range',
  },
  COMPONENT_LANDING_PAGE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'component-landing-page',
    label: 'Component Landing Page',
  },
  FULL_AUDIT_LOG_HISTORY: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'full-audit-log-history',
    label: 'Full Audit Log History',
  },
  LIST_USE_CASES_FROM_OWN_ORG: {
    label: 'List Use Cases (Ardoq Solutions) From Own Organization',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'list-use-cases-from-own-org',
  },
  ENTITY_GROUP_AND_USE_CASE_MANAGEMENT: {
    label: 'Enable Entity Group and Use Case management (for Ardoq Solutions)',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'entity-group-and-use-case-management',
  },
  AZURE_OPENAI_VIEW_INFERENCE: {
    label: 'Azure OpenAI View Inference',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'azure-openai-view-inference',
  },
  AZURE_OPENAI_COMPONENT_LINKING_SUGGESTIONS: {
    label: 'Azure OpenAI Component Linking Suggestions',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'azure-openai-component-linking-suggestions',
  },
  AZURE_AUTOMATIC_RESOURCE_GROUP_SELECTION: {
    label: 'Azure Automatic Resource Group Selection',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'azure-automatic-resource-group-selection',
  },
  DARK_MODE_TOGGLE: {
    label: 'Dark mode',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'dark-mode-toggle',
  },
  PROCESS: {
    label: 'ShiftX',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'process',
  },
  SSO_MAPPING_MULTIPLE_PROVIDERS: {
    label: 'SSO mapping multiple providers',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'sso-mapping-multiple-providers',
  },
  QUICK_START: {
    label: 'Quick Start initiative',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'quick-start',
  },
  RESTRICT_USE_CASE_DOWNLOAD: {
    label: 'Quick Start: Restrict use case download',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'restrict-use-case-download',
  },
  QUICK_START_RESTRICT_AVAILABLE_VISUALIZATIONS: {
    label: 'Quick Start: Restrict available visualizations',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'quick-start-restrict-available-visualizations',
  },
  QUICK_START_RESTRICT_AVAILABLE_INTEGRATIONS: {
    label: 'Quick Start: Restrict available integrations',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'quick-start-restrict-available-integrations',
  },
  LUCID_CHART_ENABLE: {
    label: 'Enable LucidChart integration',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'lucid-chart-enable',
  },
  LUCID_IMPORT_ENABLE: {
    label: 'Enable LucidChart import',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'lucid-import-enable',
  },
  LUCID_SCENARIO_EXPORT_ENABLE: {
    label: 'Enable LucidChart scenario export',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'lucid-scenario-export-enable',
  },
  DISABLE_CREATE_TEMPLATE_FROM_WORKSPACE_METAMODEL: {
    label: 'Disabled Legacy Feature: Create template from workspace metamodel',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'disable-create-template-workspace-metamodel',
  },
  INVENTORY_ALPHA: {
    label: 'Inventory alpha',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'inventory-alpha',
  },
  INVENTORY_REFERENCE_TABLE: {
    label: 'Shows the reference table in the inventory.',
    persistedId: 'inventory_tmp_reference-table-in-inventory-MVP',
  },
  CANVAS_PROTOTYPE: {
    label: 'Canvas Prototype',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'canvas-prototype',
  },
  CANVAS_PROTOTYPE_EDGE: {
    label: 'Canvas Prototype (in development)',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'canvas-prototype-edge',
  },
  DISABLE_INTEGRATIONS_DASHBOARD: {
    label: 'Disable integrations dashboard',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'disable-integrations-dashboard',
  },
  DISABLE_REFERENCE_CARDINALITY: {
    label: 'Disable reference cardinality',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'disable-reference-cardinality',
  },
  DISABLE_DATE_TIME_FIELDS: {
    label: 'Disable DateTime fields creation',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'disable-date-time-fields',
  },
  SURVEYS_CHANGE_APPROVAL_V2: {
    label: 'Surveys Change Approval (v2)',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'surveys-change-approval-v2',
  },
  SURVEYS_CHILD_QUESTIONS: {
    label: 'Beta. Enables child questions in surveys.',
    persistedId: 'surveys_tmp_child-questions',
  },
  USE_NEW_PRICING_MODULES: {
    label: 'Use new pricing modules',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'use-new-pricing-modules',
  },
  VISIBILITY_MODULE: {
    label: 'Visibility Module',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'visibility-module',
  },
  TRANSFORMATION_MODULE: {
    label: 'Transformation Module',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'transformation-module',
  },
  OVERSIGHT_MODULE: {
    label: 'Oversight Module',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'oversight-module',
  },
  FOUNDATION_SOLUTION_ENABLE: {
    label: 'Enable Foundation Solution',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'foundation-solution-enable',
  },
  DEV_SHOW_ALL_SOLUTIONS: {
    label: 'Dev: Show All Solutions',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'dev-show-all-solutions',
  },
  AI_VALUE_STREAMS: {
    label: 'AI Value Streams',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'ai-value-streams',
  },
  AI_CHAT: {
    label: 'AI Chat',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'ai-chat',
  },
  AI_INTERNAL_CHAT_EXPORT: {
    label: 'Internal Chat Export',
    persistedId: 'ai_internal_chat-export',
  },
  AI_WEB_SEARCH: {
    label: 'AI Web Search',
    persistedId: 'ai_restricted_web-search',
  },
  AI_CAN_ENABLE_WEB_SEARCH: {
    label: 'Can Enable Web Search',
    persistedId: 'ai_restricted_can-enable-web-search',
  },
  AI_TMP_PYTHON_AGENTS: {
    label: 'AI Python Agents',
    persistedId: 'ai_tmp_python-agents',
  },
  AI_TMP_INSIGHT_AGENT: {
    label: 'AI Chat Insight Agent',
    persistedId: 'ai_tmp_insight-agent',
  },
  AI_INTERNAL_ASSISTANT_CONFIG: {
    label: 'Internal Assistant Config',
    persistedId: 'ai_internal_assistant-config',
  },
  AI_TMP_CUSTOM_AGENT_EDITING: {
    label: 'Custom Agent Editing',
    persistedId: 'ai_tmp_custom-agent-editing',
    requiresOrgAdmin: true,
  },
  AI_TMP_ASSISTANT_VIEW_PREVIEW: {
    label: 'AI Assistant View Preview',
    persistedId: 'ai_tmp_assistant-view-preview',
  },
  AI_TMP_TRAVERSAL_BUILDING: {
    label: 'AI Traversal Building',
    persistedId: 'ai_tmp_traversal-building',
  },
  AI_FEATURES_ENABLED: {
    label: 'AI features enabled',
    persistedId: 'ai_restricted_features-enable',
  },
  DISABLED_INTERCOM: {
    label: 'Disabled Intercom',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'disabled-intercom',
  },
  DISABLE_BEAMER: {
    label: 'Disable Beamer',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'disable-beamer',
  },
  AUTOLOGIN: {
    label: 'Autologin',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'autologin',
  },
  GARTNER_DEMO_FEATURES: {
    label: 'Features for organizations related to Gartner Demo',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'gartner-demo-features',
  },
  REFERENCE_DATA_TOGAF: {
    label: 'TOGAF',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'reference-data-togaf',
  },
  REFERENCE_DATA_TOGAF_GOVERNMENT_REFERENCE_MODEL: {
    label: 'TOGAF - Government Reference Model (GRM)',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'reference-data-togaf-government-reference-model',
  },
  REFERENCE_DATA_TOGAF_TECHNICAL_REFERENCE_MODEL: {
    label: 'TOGAF - Technical Reference Model (TRM)',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'reference-data-togaf-technical-reference-model',
  },
  REFERENCE_DATA_SUPPLY_CHAIN_MANAGEMENT_ASSOCIATION_SCOR: {
    label: 'Supply Chain Management Association (SCOR)',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'reference-data-supply-chain-management-association-scor',
  },
  REFERENCE_DATA_BUSINESS_ARCHITECTURE_GUILD_BAG: {
    label: 'Business Architecture Guild Common Reference Model (BAG)',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'reference-data-business-architecture-guild-bag',
  },
  REFERENCE_DATA_BIAN_FRAMEWORK_V12: {
    label: 'BIAN Framework v12.0',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'reference-data-bian-framework-v12',
  },
  OPEN_DIGITAL_ARCHITECTURE: {
    label: 'Open Digital Architecture (ODA)',
    persistedId:
      'integrations_restricted_reference-data-open-digital-architecture',
  },
  INTEGRATIONS_CATALOG_ENRICHMENT: {
    label: 'Ardoq Catalog enrichment',
    persistedId: 'integrations_restricted_catalog-enrichment',
  },
  INTEGRATIONS_TMP_AI_AUTOMAP_COLUMNS: {
    label: 'AI automap columns in integrations',
    persistedId: 'integrations_tmp_ai-automap-columns',
  },
  DISCOVER_TMP_CONFIGURE_QUICK_LINKS: {
    label: 'Configure quick links while impersonating',
    persistedId: 'discover_tmp_configure-quick-links',
  },
  /* Discover Core Consolidation */
  DISCOVER_CORE_LANDING_PAGE_AND_NAVIGATION: {
    label: 'New Discover Core landing page and navigation',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'discover-core-landing-page-and-navigation',
  },
  DISCOVER_SCOPED_NAVIGATION: {
    label: 'Discover scoped navigation',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'discover-scoped-navigation',
  },
  /** @deprecated Use hasNewSidebarAndNavbar$ instead */
  DEPRECATED_NEW_SIDEBAR_AND_NAVBAR: {
    label: 'Enable new sidebar and navbar',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'new-sidebar-and-navbar',
  },
  HIDE_LEGACY_DISCOVER_AND_VIEWPOINTS: {
    label:
      'Hide "Open old Discover" banner, "Try new Discover" banner, and legacy Discover viewpoints',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'hide-legacy-discover-and-viewpoints',
  },
  REDIRECT_LEGACY_DISCOVER: {
    label: 'Redirect legacy /discover/* URLs to new-app equivalents',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'redirect-legacy-discover',
  },
  WORKSPACE_WIZARD_2025: {
    label: 'Workspace Wizard 2025',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'workspace-wizard-2025',
  },
  REPORT_FILTERING_IN_PRESENTATIONS: {
    label: 'Report filtering in presentations',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'tmp-report-filtering-in-presentations',
  },
  FOUNDATION_HOME: {
    label: 'Foundation Home',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'team-navigation-home',
  },
  HOME_PAGE_SETTINGS: {
    label: 'Home Page Settings',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'team-navigation-home-settings',
  },
  PRODUCT_TOUR_AGENT: {
    label: 'Product tour authoring overlay',
    persistedId: 'growth_internal_product-tour-agent',
  },
  BRANCHED_GREMLIN_GRAPH: {
    label: 'Branched Gremlin Graph',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'branched-gremlin-graph',
  },
  DASHBOARD_TMP_NEW_FILTER_TYPES: {
    label: 'New dev dashboard filter types',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'dashboard-tmp-new-filter-types',
  },
  ACCESS_CUSTOMER_SUPPORT_CHAT_PRIVILEGE: {
    label: 'Access Customer Support Chat Privilege',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'access-customer-support-chat-privilege',
  },
  AI_QUERYBUILDER: {
    label: 'AI query builder',
    persistedId: 'ai_tmp_gremlin-querybuilder',
  },
  COMPONENT_OVERVIEW_2025: {
    label: 'Component Overview 2025',
    persistedId: 'component-overview_tmp_component-overview-2025',
  },
  ROADMAP_2026: {
    label: 'WIP reimplementation of the Roadmap view',
    persistedId: 'visualizations_tmp_roadmap-2026',
  },
  WORKSPACE_MODE_SLIDES_END_ANNOUNCEMENT: {
    label: 'Announce that adding new slides in workspace mode ends',
    persistedId: 'visualizations_tmp_workspace-mode-slides-end-announcement',
  },
  ASSISTANT_DIAGRAM: {
    label: 'Assistant Diagram (beta)',
    persistedId: 'visualizations_tmp_assistant-diagram',
  },
  ASSISTANT_BLOCKS_VIEW: {
    label: 'Assistant Blocks view',
    persistedId: 'visualizations_tmp_assistant-blocks-view',
  },
  PRESENTATIONS_PPTX_EXPORT: {
    label: 'Beta. Export presentation to PPTX.',
    persistedId: 'presentations_tmp_pptx-export',
  },
  FLEXIBLE_BLOCK_DIAGRAM: {
    label: 'Flexible Block Diagram (beta)',
    persistedId: 'visualizations_tmp_flexible-block-diagram',
  },
  FLEXIBLE_BLOCK_DIAGRAM_AS_BLOCK_DIAGRAM: {
    label: 'Flexible Block Diagram replaces Block Diagram in viewpoints (beta)',
    persistedId: 'visualizations_tmp_flexible-block-diagram-as-block-diagram',
  },
  CONVERT_SLIDES_TO_VIEWPOINT: {
    label:
      'Open workspace-mode slides in viewpoint mode in presentations (beta)',
    persistedId: 'visualizations_tmp_convert-slides-to-viewpoint',
  },
  EXCLUDE_COMPONENT_FROM_VIEWPOINT: {
    label: 'Exclude a component from a viewpoint (beta)',
    persistedId: 'visualizations_tmp_exclude-component-from-viewpoint',
  },
  VIEW_DATA_LIMIT_BEFORE_BUILD: {
    label:
      'Check a view against its data limit before the view model is built, instead of after',
    persistedId: 'visualizations_tmp_data-limit-before-build',
  },
  DISABLE_NEW_DISCOVER_ADMIN_FEATURE_TOGGLE: {
    label: 'Disable new Discover admin feature toggle',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'disable-new-discover-admin-feature-toggle',
  },
  TEAM_CORE_EXPERIMENTAL_FEATURES: {
    label: 'Team core experimental features',
    persistedId: 'core_internal_experimental-features',
  },
  REUSE_EXISTING_TYPES: {
    label: 'Reuse existing component and reference types',
    persistedId: 'core_tmp_reuse-existing-types',
  },
  BLOCK_TYPE_AND_FIELD_CREATION: {
    label: 'Block component and reference type and field creation',
    persistedId: 'core_tmp_block-type-and-field-creation',
  },
  GLOBAL_COMPONENT_TYPES_ALPHA: {
    label: 'Global component types (alpha)',
    persistedId: 'core_tmp_global-component-types-alpha',
  },
  GLOBAL_COMPONENT_TYPES_BETA: {
    label: 'Global component types (beta)',
    persistedId: 'core_tmp_global-component-types-beta',
  },
  SOLUTIONS_METAMODEL_LINK: {
    label: 'Metamodel link on solutions page',
    persistedId: 'solutions_tmp_metamodel-link',
  },
  PREFER_OPENING_WORKSPACES_IN_INVENTORY: {
    label: 'Open workspace data in inventory by default instead of workspace',
    persistedId: 'inventory_tmp_prefer-opening-workspaces-in-inventory',
  },
  PREFER_OPENING_WORKSPACES_IN_WORKSPACE: {
    label: 'Open workspace data in workspace by default instead of inventory',
    persistedId: 'core_tmp_prefer-opening-workspaces-in-workspace',
  },
  GRAPHLAKE_ALPHA: {
    label: 'Graphlake alpha',
    persistedId: 'core_tmp_graphlake-alpha',
  },
  GRAPHLAKE_TIME_TRAVEL: {
    label: 'GraphLake time travel',
    persistedId: 'graphlake_tmp_time-travel',
  },
  ENABLE_ONBOARDING_STEPS: {
    label: 'Enable Onboarding Steps Widget',
    // @ts-expect-error Intentionally omits domain_scope prefix
    persistedId: 'enable-onboarding-steps',
  },
  AUDIT_LOG_FILTER_BUTTON: {
    label: 'Audit log filter button',
    persistedId: 'audit-log_tmp_filter-button',
  },
  REFERENCE_BUBBLE_CHART_IN_VIEWPOINT_MODE: {
    label: 'Reference Bubble Chart in Viewpoint Mode',
    persistedId: 'visualizations_tmp_reference-bubble-chart-in-viewpoint-mode',
  },
  SURFACE_3D_VIEW: {
    label: '3D Surface Chart view',
    persistedId: 'visualizations_tmp_surface-3d-view',
  },
  CONTOUR_VIEW: {
    label: 'Contour Chart view',
    persistedId: 'visualizations_tmp_contour-view',
  },
  SCATTER_3D_VIEW: {
    label: '3D Scatter Chart view',
    persistedId: 'visualizations_tmp_scatter-3d-view',
  },
  BUBBLE_3D_VIEW: {
    label: '3D Bubble Chart view',
    persistedId: 'visualizations_tmp_bubble-3d-view',
  },
  NETWORK_3D_VIEW: {
    label: '3D Network view',
    persistedId: 'visualizations_tmp_network-3d-view',
  },
  SPIDER_2_VIEW: {
    label: 'Spider Chart view in viewpoint mode',
    persistedId: 'visualizations_tmp_spider-2-view',
  },
  POLAR_VIEW: {
    label: 'Polar Chart view',
    persistedId: 'visualizations_tmp_polar-view',
  },
  LANGUAGE_SELECTION: {
    label: 'Enable Beta UI language options',
    persistedId: 'core_restricted_ui-language-selection',
  },
  TRANSFORMATIONS: {
    label: 'WIP Transform',
    persistedId: 'transformations_tmp_transformations',
  },
  TRANSFORMATIONS_ALPHA: {
    label: 'WIP Transform alpha features',
    persistedId: 'transformations_tmp_alpha',
  },
  CONTENT_AWARE_BUNDLE_COPY: {
    label: 'Content-aware bundle copy',
    persistedId: 'core_tmp_content-aware-bundle-copy',
  },
  PERSON_IN_GRAPH_PERMISSIONS_PROTOTYPE: {
    label: 'Person in Graph permissions prototype',
    persistedId: 'access_tmp_person-in-graph-permissions-prototype',
  },
  VISUALIZATIONS_TMP_AI_VIEW_CONFIGURATION_EXTENDED: {
    label: 'AI view configuration (for labels, viewsettings, grouping)',
    persistedId: 'visualizations_tmp_ai-view-configuration-extended',
  },
  ASSETS_OVERVIEW_AG_GRID: {
    label: 'Render asset overview pages with the new table component',
    persistedId: 'design-system_tmp_assets-overview-ag-grid',
  },
  LOCKED_COMPONENTS: {
    label: 'Enable locked components',
    persistedId: 'core_phased_locked-components',
  },
  REFERENCE_CREATION_NOODLE: {
    label: 'Drag to create reference in viewpoints',
    persistedId: 'core-viewpoints_tmp_reference-creation-noodle',
  },
  REFERENCE_CREATION_NOODLE_WITH_SEARCH: {
    label: 'Adds a search box for reference creation with noodle',
    persistedId: 'core-viewpoints_tmp_quick-reference-search',
  },
  USAGE_METRICS_TMP_EXPORT_CSV: {
    label: 'Usage metrics export CSV download button',
    persistedId: 'dashboards_tmp_usage-metrics-export-csv',
  },
  DASHBOARDS_TMP_POWERBI_EMBED: {
    label: 'PowerBI embed widget in dashboards',
    persistedId: 'dashboards_tmp_powerbi-embed',
  },
  HARD_NAX: {
    label: 'Hard NAX',
    persistedId: 'core_tmp_hard-nax',
  },
  HARD_NAX_EXPERIMENTAL: {
    label: 'Hard NAX (experimental)',
    persistedId: 'core_tmp_hard-nax-experimental',
  },
  IMPORT_BUILDER_AI: {
    label: 'Import Builder AI Configure',
    persistedId: 'integrations_restricted_import-builder-ai-configure',
  },
  SANDBOX_INDICATOR: {
    label: 'Sandbox indicator',
    persistedId: 'core_tmp_sandbox-indicator',
  },
  IMPROVED_DATASET_DEFINITION: {
    label: 'Improved dataset definition',
    persistedId: 'core_tmp_improved-dataset-definition',
  },
  GROWTH_TMP_DASHBOARD_SAVE_BUBBLE_CHART_WIDGET: {
    label: 'Support saving bubble chart as a widget',
    persistedId: 'growth_tmp_dashboard-save-bubble-chart-widget',
  },
  VISIBILITY_TMP_APPLICATION_COMPLETENESS_PAGE: {
    label: 'Application completeness page',
    persistedId: 'visibility_tmp_application-completeness-page',
  },
  GROWTH_TMP_EXCLUDE_FROM_NAX_MIGRATION_DEADLINE: {
    label: 'Exclude from NAX migration deadline',
    persistedId: 'growth_tmp_exclude-from-nax-migration-deadline',
  },
  CORE_TMP_SYSTEM_OVERVIEW: {
    label: 'System overview',
    persistedId: 'core_tmp_system-overview',
  },
  SIGNAL_INTERNAL_WIP: {
    label: 'Signal WIP',
    persistedId: 'signal_internal_wip',
  },
  SIGNAL_TMP_AUTO_LINKING_BETA: {
    label: 'Beta. Auto-linking',
    persistedId: 'signal_tmp_auto-linking-beta',
  },
  CORE_TMP_GLOBAL_WORKSPACE_TABLE: {
    label:
      'Shows the metamodel landing page and the organization-wide workspaces table.',
    persistedId: 'core_tmp_global-workspace-table',
  },
  CORE_TMP_METAMODEL_GRAPH: {
    label:
      'Beta. Shows the organization metamodel as a searchable graph of component types and references.',
    persistedId: 'core_tmp_metamodel-graph',
  },
  DASHBOARDS_RESTRICTED_DISABLE_USAGE_METRICS: {
    label: 'Disable usage metrics',
    persistedId: 'dashboards_restricted_disable-usage-metrics',
  },
  CORE_TMP_FIELD_PAGE_V2: {
    label: 'Beta. Shows the new version of the field page.',
    persistedId: 'core_tmp_field-page-v2',
  },
  CORE_TMP_FIELDS_COMPONENT_TYPE_FILTER: {
    label:
      'Beta. Adds a sidebar to the organization fields page for filtering fields by component type and workspace.',
    persistedId: 'core_tmp_fields-component-type-filter',
  },
  CORE_TMP_FIELD_VISIBILITY: {
    label: 'Field group visibility on the component overview page',
    persistedId: 'core_tmp_field-visibility',
  },
  VISUALIZATIONS_TMP_TRAVERSAL_SHAPED_HIERARCHY: {
    label:
      'Dependency Map and Timeline in viewpoint mode follow the traversal: only grouping branches that lead to the start set, without links to enclosing groups',
    persistedId: 'visualizations_tmp_traversal-shaped-hierarchy',
  },
  PARAMETERIZED_PRESENTATIONS: {
    label: 'Re-render presentation slides for a different context component',
    persistedId: 'presentations_tmp_parametrized-presentations',
  },
  GROWTH_TMP_HOMEPAGE_PAGE_BUILDER: {
    label: 'Edit the homepage with the page builder',
    persistedId: 'growth_tmp_homepage-page-builder',
  },
  SHIFTX_NEW_ACCESS_MODEL: {
    label:
      'New ShiftX access model, where what users see and sync is no longer limited by their Workspace permissions in Ardoq',
    persistedId: 'bpm_tmp_new-access-model',
  },
  CORE_TMP_ATTACHMENT_DEDUPLICATION: {
    label: 'Find and remove duplicate attachments in the document archive',
    persistedId: 'core_tmp_attachment-deduplication',
  },
  VIEWPOINT_DECLARATIVE_ROUTER: {
    label: 'WIP. Open viewpoint mode URLs on the declarative viewpoint route',
    persistedId: 'core-viewpoints_tmp_viewpoint-declarative-router',
  },
} satisfies Record<string, StrictFeature>;

export type FeatureKeys = keyof typeof features;

export const Features: Record<FeatureKeys, Feature> = features;

export const FeatureName = Object.fromEntries(
  (Object.keys(Features) as FeatureKeys[]).map(
    featureKey => [featureKey, featureKey] as const
  )
) as Record<FeatureKeys, FeatureKeys>;
